import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import type { InputProps } from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { inputLabelClasses } from "@mui/material/InputLabel";
import { rgbToHex } from "@mui/material/styles";
import type { OutlinedTextFieldProps, StandardTextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ColorPicker from "common/components/Elements/ColorPicker/ColorPicker";
import { Messages } from "common/components/Elements/ExpandableMessages/ExpandableMessages";
import { hasContentForMessages } from "common/components/Elements/ExpandableMessages/utils.ts";
import InputAdormentLoading from "common/components/Elements/Form/InputAdormentLoading/InputAdormentLoading";
import InputDynamicLabel from "common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel";
import type { TooltipHelp } from "common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp";
import InputTooltipHelp from "common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp";
import InputUnit from "common/components/Elements/Form/InputUnit/InputUnit";
import SanitizeHTML from "common/components/Elements/SanitizeHTML/SanitizeHTML";
import UnitDropdown from "common/components/Elements/UnitDropdown/UnitDropdown";
import { hexToRgb, rgbToCSS } from "common/utilities/colors/colors";
import { generateRandomString, stringContainsLineBreak } from "common/utilities/core/string";
import useRect from "common/utilities/hooks/useRect/useRect";
import type * as I from "core/api/coreApiClient";
import { inputStyles, inTableInputMarginTop } from "core/utilities/styles/input.styles";
import theme, { sx, sxClassName, ThemeVars } from "core/utilities/theme";
import debounce from "debounce";
import type { Ref } from "react";
import React, { useState } from "react";

export interface ComponentProps {
  name?: string;
  title?: string;
  /**
   * See possible values: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   * The 'name' attribute needs to be provided to make it work.
   */
  autoComplete?: string;
  // Value is mandatory (set empty string when no value)
  // Why: if the initial value is undefined, the textfield is 'uncontrolled',
  // meaning that if a value is sent afterwards it will become 'controlled' and trigger an error
  value: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (value: string, unit?: string) => void;
  onBlur?: (value: string, unit?: string) => void;
  errorMsg?: I.Message[];
  e2eId?: string;
  fullWidth?: boolean;
  outlined?: boolean;
  tooltipHelp?: TooltipHelp;
  autoFocus?: boolean;
  clearable?: boolean;
  unit?: string;
  units?: I.Unit[];
  inputRef?: Ref<HTMLInputElement>;
  longFormStyle?: boolean;
  multiline?: boolean;
  multilineMinRows?: number;
  multilineMaxRows?: number;
  sideLabel?: boolean;
  modalLabel?: boolean;
  role?: string;
  loading?: boolean;
  enableLastpass?: boolean;
  helpInfo?: I.Denotation;
  colorPicker?: boolean;
  textSmall?: boolean;
  inTable?: boolean;
  connectingLine?: boolean;
  /** Input field of type password (with toggle to show/hide password while typing it) */
  password?: boolean;
  afterFieldElement?: JSX.Element;
}

function InputText({
  label,
  errorMsg,
  onChange,
  disabled,
  required,
  value,
  unit,
  units,
  inputRef,
  placeholder,
  e2eId,
  fullWidth,
  onBlur,
  outlined,
  tooltipHelp,
  autoFocus,
  clearable,
  longFormStyle,
  name,
  autoComplete,
  multiline,
  sideLabel,
  modalLabel,
  role,
  multilineMinRows,
  multilineMaxRows,
  loading,
  title,
  enableLastpass,
  helpInfo,
  colorPicker,
  textSmall,
  inTable,
  connectingLine,
  password,
  afterFieldElement,
}: ComponentProps) {
  const styles = inputStyles({
    longFormStyle,
    sideLabel,
    modalLabel,
    errorMsg,
    inTable,
    marginTop: inTableInputMarginTop.InputText,
  });

  const [id] = useState(() => generateRandomString());
  const [inputAdornmentRect, inputAdornmentRef] = useRect<HTMLDivElement>();
  const [showPassword, setShowPassword] = useState(false);

  const showUnit = longFormStyle && unit && value !== undefined && value.length > 0;
  const getUnitLabel = () => {
    const selectedUnit = units?.find((u) => u.id === unit);
    if (selectedUnit) {
      if (selectedUnit.htmlLabel) {
        return <SanitizeHTML html={selectedUnit.htmlLabel} />;
      }
      return selectedUnit.label;
    }
    return undefined;
  };

  const variantProps = outlined ? { variant: "outlined" as const } : { variant: "standard" as const };

  const inputProps: Partial<InputProps> = {};
  inputProps.sx = sx(
    colorPicker ? styles.shortInputRoot : styles.inputRoot,
    textSmall ? { fontSize: ThemeVars.badgeFontSize } : undefined,
    inTable ? { mt: -0.5 } : undefined,
  );

  inputProps.endAdornment = (
    <InputAdornment position="end" ref={inputAdornmentRef}>
      {showUnit && (
        <Typography
          component="span"
          sx={sx(styles.inputRoot, {
            paddingRight: units && units.length > 1 ? "33px" : 0, // simulate change unit icon size
            visibility: "hidden",
          })}
        >
          {getUnitLabel()}
        </Typography>
      )}
      {colorPicker && (
        <ColorPicker
          color={value ? hexToRgb(value) : undefined}
          onChange={(newColor) => {
            onChange(rgbToHex(rgbToCSS(newColor)));
          }}
          notTriggeredFromModal
        />
      )}
      {password && (
        <IconButton
          aria-label="toggle password visibility"
          onClick={() => setShowPassword(!showPassword)}
          onMouseDown={(e) => e.preventDefault()}
          data-testid="toggleVisibility"
          size="large"
        >
          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </IconButton>
      )}
      {clearable && disabled !== true && value.length > 0 && (
        <Box mr={-1}>
          <IconButton onClick={() => onChange("")} data-e2e-form-textfield-reset="" size="small">
            <ClearIcon style={{ fontSize: "16px" }} />
          </IconButton>
        </Box>
      )}
      {tooltipHelp && <InputTooltipHelp tooltipHelp={tooltipHelp} />}
    </InputAdornment>
  );

  if (loading) {
    inputProps.endAdornment = <InputAdormentLoading inputAdornmentRef={inputAdornmentRef} />;
  }

  let formattedLabel: string | JSX.Element = label || "";

  if (longFormStyle && getUnitLabel() && (value === undefined || value.length === 0)) {
    formattedLabel = (
      <Box>
        {`${formattedLabel} `}
        <Box sx={{ display: "inline-block", whiteSpace: "nowrap" }}>
          [{getUnitLabel()}
          {units && units.length > 1 && (
            <UnitDropdown currentUnitId={unit} units={units} onChange={(newUnit) => onChange(value || "", newUnit)} />
          )}
        </Box>
        ]
      </Box>
    );
  }

  const props: StandardTextFieldProps | OutlinedTextFieldProps = {
    sx: styles.root,
    id,
    name,
    type: !password || showPassword ? "text" : "password",
    role,
    // The value "current-password" is mandatory to trigger browsers' "save password" feature
    autoComplete: password ? "current-password" : autoComplete || "off",
    error: errorMsg && errorMsg.length > 0,
    disabled,
    required,
    autoFocus,
    label: sideLabel ? undefined : formattedLabel,
    placeholder,
    value,
    helperText: hasContentForMessages(errorMsg, helpInfo) ? (
      <Messages messages={errorMsg} denotation={helpInfo} />
    ) : undefined,
    title,
    onChange: (e) => {
      /**
       * Fix for Firefox autofill not working with React
       * https://github.com/facebook/react/issues/18986
       */
      Object.defineProperty(e.target, "defaultValue", {
        configurable: true,
        get() {
          return "";
        },
        set() {},
      });

      onChange(e.target.value, unit);
    },
    margin: "dense",
    size: "small",
    onBlur: (e) => {
      if (onBlur) {
        onBlur(e.target.value);
      }
    },
    ...variantProps,
    inputProps: {
      "aria-multiline": stringContainsLineBreak(value) || multiline,
      "data-lpignore": enableLastpass ? undefined : "true",
    },
    InputProps: inputProps,
    fullWidth,
    inputRef,
    multiline: stringContainsLineBreak(value) || multiline,
    minRows: multilineMinRows || 3, // Multiline fields will always show a minimum of 3 rows
    maxRows: multilineMaxRows || 100, // Multiline fields will always show a maximum of 100 rows
    InputLabelProps: {
      htmlFor: id,
      sx: {
        [sxClassName(inputLabelClasses.root)]: styles.labelRoot,
        [sxClassName(inputLabelClasses.disabled)]: styles.labelDisabled,
        [sxClassName(inputLabelClasses.shrink)]: styles.labelShrink,
      },
    },
  };

  const InputTextWithOptionalUnit = (
    <Box sx={{ display: "inline-flex", flexGrow: 1, width: "100%" }}>
      <Box position="relative" width="100%">
        {showUnit && (
          <InputUnit
            value={value}
            unit={unit}
            units={units}
            longFormStyle={longFormStyle}
            disabled={disabled}
            sideLabel={sideLabel}
            inputAdornmentWidth={inputAdornmentRect?.width || 0}
            onChange={(newUnit) => onChange(value || "", newUnit)}
            inTable={inTable}
          />
        )}
        <TextField {...props} data-e2e-form-textfield={e2eId} />
      </Box>
      {afterFieldElement}
    </Box>
  );

  return (
    <InputDynamicLabel
      sideLabel={sideLabel}
      htmlFor={id}
      disabled={disabled}
      errorMsg={errorMsg}
      label={formattedLabel}
      required={required}
      testid="InputText"
      title={title}
    >
      {connectingLine ? (
        <Box sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
          <Box
            sx={{
              mr: 1,
              mb: 0.5,
              borderLeft: `1px dotted ${theme.palette.text.disabled}`,
              borderBottom: `1px dotted ${theme.palette.text.disabled}`,
              width: "26px",
            }}
          />
          <Box sx={{ flexGrow: 1 }}>{InputTextWithOptionalUnit}</Box>
        </Box>
      ) : (
        InputTextWithOptionalUnit
      )}
    </InputDynamicLabel>
  );
}

export default InputText;

export const INPUT_TEXT_DEBOUNCE = 300;

const sendDebouncedValue = debounce(
  (onChange: (value: string) => void, newValue: string) => onChange(newValue),
  INPUT_TEXT_DEBOUNCE,
);

/**
 * Debounced the InputText Component
 */
export function DebouncedInputText(props: ComponentProps) {
  const { value, onChange } = props;
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  const handleOnChange = React.useCallback(
    (newValue: string) => {
      setDebouncedValue(newValue);
      sendDebouncedValue(onChange, newValue);
    },
    [setDebouncedValue, onChange],
  );

  React.useEffect(() => {
    if (value !== debouncedValue) {
      handleOnChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOnChange, value]);

  return <InputText {...props} value={debouncedValue} onChange={handleOnChange} />;
}
